/**
 * @file CredentialingChatWidget.tsx
 * @description Full-height chat interface for the credentialing knowledge chatbot.
 *              Consumes SSE from /api/credentialing-chat, renders markdown responses,
 *              handles email gating and rate limiting. Designed to fill an iframe
 *              on the marketing site or a sidebar in the app.
 * @author William Donaldson
 * @last_modified 2026-04-13
 */

"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { buildHistory } from "@/lib/chatbot/buildHistory";
import { STARTER_CHIPS } from "@/lib/chatbot/starterChips";

// =============================================================================
// TYPES
// =============================================================================

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  source?: string;
  /** Clickable option chips (e.g., state selection for BCBS disambiguation) */
  chips?: { label: string; value: string }[];
}

interface RateLimitInfo {
  remaining: number;
  limit: number;
  requiresEmail: boolean;
}

interface Props {
  /** "public" = email gate, rate limiting, CTA. "app" = none of that. */
  mode?: "public" | "app";
}

// =============================================================================
// CONSTANTS
// =============================================================================

const API_URL = "/api/credentialing-chat";
const CAPTURE_URL = "/api/credentialing-chat/capture-lead";
const SESSION_STATUS_URL = "/api/credentialing-chat/session-status";

// WHY IMPORT: Shared with the cache warm-up cron so both target the exact
// same question text. See src/lib/chatbot/starterChips.ts.

// =============================================================================
// COMPONENT
// =============================================================================

export default function CredentialingChatWidget({ mode = "public" }: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [hasCapturedLead, setHasCapturedLead] = useState(false);
  const [showEmailGate, setShowEmailGate] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  const [isInitializing, setIsInitializing] = useState(mode === "public");
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo>({
    remaining: 5,
    limit: 5,
    requiresEmail: false,
  });
  const [rateLimitExhausted, setRateLimitExhausted] = useState(false);

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);

  // WHY REF MIRROR: sendQuestion needs the latest messages to build history,
  // but adding `messages` to useCallback deps would recreate the callback on
  // every streaming token. A ref avoids stale closures without dep churn.
  const messagesRef = useRef<ChatMessage[]>(messages);
  messagesRef.current = messages;

  // Auto-scroll to latest message
  // WHY scrollTop INSTEAD OF scrollIntoView: scrollIntoView bubbles up to every
  // scrollable ancestor, including the document body. In an iframe on the
  // marketing site this causes the browser viewport itself to scroll, pushing the
  // chat widget out of view. Setting scrollTop directly on the overflow container
  // is strictly scoped and cannot affect parent frames.
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    if (!isInitializing) {
      inputRef.current?.focus();
    }
  }, [isInitializing]);

  // ---------------------------------------------------------------------------
  // SESSION INIT — returning user recognition
  // ---------------------------------------------------------------------------

  // WHY ON MOUNT: Widget state resets on every page load. Without this, returning
  // users who already captured would be treated as anonymous (5-question limit).
  useEffect(() => {
    if (mode !== "public") {
      setIsInitializing(false);
      return;
    }

    let cancelled = false;

    async function initSession() {
      try {
        const res = await fetch(SESSION_STATUS_URL, {
          credentials: "include",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        if (cancelled) return;

        setHasCapturedLead(data.hasLead ?? false);
        setRateLimitInfo({
          remaining: data.remaining ?? 5,
          limit: data.limit ?? 5,
          requiresEmail: false,
        });
      } catch {
        // WHY SILENT FALLBACK: Init failure means the widget uses anonymous
        // defaults. The user can still use the chatbot normally.
      } finally {
        if (!cancelled) setIsInitializing(false);
      }
    }

    void initSession();
    return () => { cancelled = true; };
  }, [mode]);

  // WHY PROACTIVE GATE: The email gate was only shown on a 429 server response,
  // but nothing blocked the user from sending messages after the client counter
  // hit 0. This effect triggers the gate as soon as remaining reaches 0, before
  // the user can send another message.
  useEffect(() => {
    if (
      mode === "public" &&
      !hasCapturedLead &&
      rateLimitInfo.remaining <= 0 &&
      !showEmailGate &&
      messages.length > 0
    ) {
      setShowEmailGate(true);
      setMessages((prev) => [
        ...prev,
        {
          id: `limit-${Date.now()}`,
          role: "assistant",
          content:
            "You've reached the free question limit. Enter your email below to unlock more questions!",
        },
      ]);
    }
  }, [rateLimitInfo.remaining, hasCapturedLead, mode, showEmailGate, messages.length]);

  // Focus email input when gate appears
  useEffect(() => {
    if (showEmailGate) {
      emailInputRef.current?.focus();
    }
  }, [showEmailGate]);

  // ---------------------------------------------------------------------------
  // SSE HANDLER
  // ---------------------------------------------------------------------------

  const sendQuestion = useCallback(
    async (question: string) => {
      if (!question.trim() || isStreaming) return;

      // WHY GUARD HERE: Belt-and-suspenders check in case the useEffect hasn't
      // fired yet (e.g., rapid clicks). Shows the gate instead of sending.
      if (mode === "public" && !hasCapturedLead && rateLimitInfo.remaining <= 0) {
        setShowEmailGate(true);
        return;
      }

      // WHY BEFORE setMessages: buildHistory must capture prior turns only.
      // The current question is already in the `question` field.
      const history = buildHistory(messagesRef.current);

      // Add user message
      const userMsg: ChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: question.trim(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setInput("");
      setIsStreaming(true);

      // Placeholder for assistant response
      const assistantId = `assistant-${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        { id: assistantId, role: "assistant", content: "" },
      ]);

      try {
        const response = await fetch(API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            question: question.trim(),
            ...(history.length > 0 && { history }),
          }),
        });

        // Handle rate limit response (not SSE)
        if (response.status === 429) {
          const data = await response.json();
          if (data.requiresEmail && mode === "public") {
            setShowEmailGate(true);
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? {
                      ...m,
                      content:
                        "You've reached the free question limit. Enter your email below to unlock more questions!",
                    }
                  : m
              )
            );
          } else {
            setRateLimitExhausted(true);
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? {
                      ...m,
                      content:
                        "You've reached your daily question limit. Please try again tomorrow!",
                    }
                  : m
              )
            );
          }
          setRateLimitInfo({
            remaining: data.remaining ?? 0,
            limit: data.limit ?? 5,
            requiresEmail: data.requiresEmail ?? false,
          });
          setIsStreaming(false);
          return;
        }

        if (!response.ok || !response.body) {
          throw new Error(`HTTP ${response.status}`);
        }

        // Parse SSE stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n\n");
          // Keep the last incomplete chunk in the buffer
          buffer = lines.pop() ?? "";

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const jsonStr = line.slice(6);

            try {
              const event = JSON.parse(jsonStr);

              if (event.type === "token") {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantId
                      ? { ...m, content: m.content + event.content }
                      : m
                  )
                );
              } else if (event.type === "chips") {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantId
                      ? { ...m, chips: event.chips }
                      : m
                  )
                );
              } else if (event.type === "done") {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantId ? { ...m, source: event.source } : m
                  )
                );
                // Decrement remaining count
                setRateLimitInfo((prev) => ({
                  ...prev,
                  remaining: Math.max(0, prev.remaining - 1),
                }));
              } else if (event.type === "error") {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantId
                      ? { ...m, content: event.message }
                      : m
                  )
                );
              }
            } catch {
              // Skip malformed SSE frames
            }
          }
        }
      } catch {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? {
                  ...m,
                  content:
                    "Sorry, something went wrong. Please try again in a moment.",
                }
              : m
          )
        );
      } finally {
        setIsStreaming(false);
        inputRef.current?.focus();
      }
    },
    [isStreaming, hasCapturedLead, mode, rateLimitInfo.remaining]
  );

  // ---------------------------------------------------------------------------
  // EMAIL GATE
  // ---------------------------------------------------------------------------

  const handleEmailSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setEmailError(null);

      const trimmed = emailInput.trim().toLowerCase();
      if (!trimmed) {
        setEmailError("Please enter your email address");
        return;
      }

      // WHY NATIVE VALIDATION: Uses the browser's built-in email validation
      // via input.checkValidity(). Avoids importing Zod in the iframe bundle.
      const emailEl = emailInputRef.current;
      if (emailEl && !emailEl.checkValidity()) {
        setEmailError("Please enter a valid email address");
        return;
      }

      setIsSubmittingEmail(true);

      try {
        const res = await fetch(CAPTURE_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email: trimmed }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setEmailError(
            data.error === "Invalid email address"
              ? "Please enter a valid email address"
              : "Something went wrong. Please try again."
          );
          return;
        }

        const data = await res.json();

        // WHY SERVER VALUES: The server returns authoritative remaining/limit
        // so the widget never hardcodes quota or gets out of sync.
        setHasCapturedLead(true);
        setShowEmailGate(false);
        setRateLimitInfo({
          remaining: data.remaining ?? 15,
          limit: data.limit ?? 20,
          requiresEmail: false,
        });
        setEmailInput("");
        setEmailError(null);
      } catch {
        setEmailError("Something went wrong. Please try again.");
      } finally {
        setIsSubmittingEmail(false);
      }
    },
    [emailInput]
  );

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  const isEmpty = messages.length === 0;

  return (
    <div className="flex h-full flex-col bg-white font-[family-name:var(--font-outfit)]">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-gray-200 bg-[#0A9B72] px-4 py-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20">
          <svg
            className="h-5 w-5 text-white"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z"
            />
          </svg>
        </div>
        <div>
          <h1 className="text-sm font-semibold text-white">
            EnrollPilot Credentialing Assistant
          </h1>
          <p className="text-xs text-white/80">
            AI-powered credentialing guidance
          </p>
        </div>
      </div>

      {/* Message list */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-4 py-4">
        {isEmpty ? (
          <EmptyState onChipClick={sendQuestion} disabled={isInitializing} />
        ) : (
          <div className="space-y-4">
            {messages.map((msg, i) => (
              <MessageBubble
                key={msg.id}
                message={msg}
                isStreaming={
                  isStreaming &&
                  msg.content === "" &&
                  msg.role === "assistant"
                }
                userQuestion={
                  msg.role === "assistant" &&
                  messages[i - 1]?.role === "user"
                    ? messages[i - 1]?.content
                    : undefined
                }
                onChipClick={sendQuestion}
                chipsDisabled={isStreaming}
              />
            ))}
          </div>
        )}
      </div>

      {/* Email gate */}
      {showEmailGate && mode === "public" && (
        <EmailGate
          emailInput={emailInput}
          emailError={emailError}
          isSubmitting={isSubmittingEmail}
          emailInputRef={emailInputRef}
          onEmailChange={setEmailInput}
          onSubmit={handleEmailSubmit}
          onClearError={() => setEmailError(null)}
        />
      )}

      {/* Input */}
      <div className="border-t border-gray-200 bg-white px-4 py-3">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendQuestion(input);
          }}
          className="flex gap-2"
        >
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              isInitializing
                ? "Loading..."
                : rateLimitExhausted
                  ? "Daily limit reached"
                  : showEmailGate
                    ? "Enter your email to continue"
                    : "Ask about credentialing..."
            }
            disabled={isStreaming || rateLimitExhausted || showEmailGate || isInitializing}
            maxLength={500}
            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#0A9B72] focus:outline-none focus:ring-1 focus:ring-[#0A9B72] disabled:opacity-50"
            aria-label="Chat message input"
          />
          <button
            type="submit"
            disabled={isStreaming || rateLimitExhausted || showEmailGate || isInitializing || !input.trim()}
            className="rounded-lg bg-[#0A9B72] px-4 py-2 text-sm font-medium text-white hover:bg-[#088a64] disabled:opacity-50 transition-colors"
            aria-label="Send message"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5"
              />
            </svg>
          </button>
        </form>

        {/* Footer with remaining count */}
        {mode === "public" && (
          <p className="mt-2 text-center text-xs text-gray-400">
            {rateLimitInfo.remaining} of {rateLimitInfo.limit} questions
            remaining today
            {hasCapturedLead && " (email verified)"}
          </p>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

function EmptyState({
  onChipClick,
  disabled,
}: {
  onChipClick: (q: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center px-4 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#0A9B72]/10">
        <svg
          className="h-8 w-8 text-[#0A9B72]"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456Z"
          />
        </svg>
      </div>
      <h2 className="mb-2 text-lg font-semibold text-gray-900">
        Ask about credentialing
      </h2>
      <p className="mb-6 max-w-sm text-sm text-gray-500">
        Get instant answers about credentialing requirements, enrollment
        timelines, and forms for 100+ insurance payers and credentialing topics.
      </p>
      <div className="flex flex-wrap justify-center gap-2">
        {STARTER_CHIPS.map((chip) => (
          <button
            key={chip}
            onClick={() => onChipClick(chip)}
            disabled={disabled}
            className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:border-[#0A9B72] hover:text-[#0A9B72] transition-colors disabled:opacity-50"
          >
            {chip}
          </button>
        ))}
      </div>
      <p className="mt-6 text-xs text-gray-400">
        Powered by AI. Answers are based on our credentialing knowledge base and
        may not reflect the latest changes.
      </p>
    </div>
  );
}

function EmailGate({
  emailInput,
  emailError,
  isSubmitting,
  emailInputRef,
  onEmailChange,
  onSubmit,
  onClearError,
}: {
  emailInput: string;
  emailError: string | null;
  isSubmitting: boolean;
  emailInputRef: React.RefObject<HTMLInputElement | null>;
  onEmailChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onClearError: () => void;
}) {
  return (
    <div className="border-t border-gray-200 bg-gradient-to-b from-[#0A9B72]/5 to-white px-4 py-5">
      <div className="flex flex-col items-center text-center">
        {/* Envelope icon */}
        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-[#0A9B72]/10">
          <svg
            className="h-5 w-5 text-[#0A9B72]"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
            />
          </svg>
        </div>

        <h3 className="text-base font-semibold text-gray-900">
          Unlock 15 more questions
        </h3>
        <p className="mt-1 max-w-xs text-sm text-gray-500">
          Enter your email to continue chatting and get occasional credentialing
          tips.
        </p>
      </div>

      <form onSubmit={onSubmit} className="mt-4 space-y-3">
        <div>
          <input
            ref={emailInputRef}
            type="email"
            value={emailInput}
            onChange={(e) => {
              onEmailChange(e.target.value);
              if (emailError) onClearError();
            }}
            placeholder="you@company.com"
            disabled={isSubmitting}
            maxLength={254}
            className={`w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-1 disabled:opacity-50 ${
              emailError
                ? "border-red-400 focus:border-red-400 focus:ring-red-400"
                : "border-gray-300 focus:border-[#0A9B72] focus:ring-[#0A9B72]"
            }`}
            aria-label="Email address"
            aria-invalid={!!emailError}
            aria-describedby={emailError ? "email-error" : undefined}
          />
          {emailError && (
            <p id="email-error" className="mt-1.5 text-xs text-red-600" role="alert">
              {emailError}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#0A9B72] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#088a64] disabled:opacity-50 transition-colors"
        >
          {isSubmitting ? (
            <>
              <svg
                className="h-4 w-4 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              Submitting...
            </>
          ) : (
            <>
              Continue chatting
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                />
              </svg>
            </>
          )}
        </button>
      </form>

      <p className="mt-3 text-center text-xs text-gray-400">
        <svg
          className="mr-1 inline-block h-3 w-3"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
          />
        </svg>
        Unsubscribe anytime. We respect your inbox.
      </p>
    </div>
  );
}

/** WHY ESCAPEHTML: user questions are inserted into HTML for rich-text clipboard copy;
 * must escape to prevent injection if question contains HTML special chars */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function MessageBubble({
  message,
  isStreaming,
  userQuestion,
  onChipClick,
  chipsDisabled,
}: {
  message: ChatMessage;
  isStreaming: boolean;
  userQuestion?: string;
  onChipClick?: (value: string) => void;
  chipsDisabled?: boolean;
}) {
  const isUser = message.role === "user";
  const contentRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  /** WHY RICH COPY: copies HTML so formatting (bold, bullets, etc.) is preserved
   * when pasted into Facebook groups or other rich-text platforms for marketing */
  const handleCopy = async () => {
    // WHY CLONE + STRIP: Remove the CTA promotional footer from copied content
    // so users only copy the answer itself, not the marketing line
    let renderedHtml = "";
    if (contentRef.current) {
      const clone = contentRef.current.cloneNode(true) as HTMLElement;
      const lastHr = clone.querySelector("hr:last-of-type");
      if (lastHr) {
        while (lastHr.nextSibling) lastHr.nextSibling.remove();
        lastHr.remove();
      }
      renderedHtml = clone.innerHTML;
    }

    const htmlParts = [
      userQuestion
        ? `<p><em>"${escapeHtml(userQuestion)}"</em></p>`
        : "",
      renderedHtml,
      `<p style="color:#888;font-size:0.85em;margin-top:8px">Info provided by <a href="https://enrollpilot.com" style="color:#0A9B72;text-decoration:none">EnrollPilot</a></p>`,
    ];
    const fullHtml = htmlParts.filter(Boolean).join("\n");

    let contentForCopy = message.content;
    // WHY PREFIX MATCH: The CTA wording may change but always starts with this prefix.
    const ctaIdx = contentForCopy.indexOf("\n\n---\n*EnrollPilot");
    if (ctaIdx !== -1) {
      contentForCopy = contentForCopy.slice(0, ctaIdx);
    }

    const textParts = [
      userQuestion ? `"${userQuestion}"` : "",
      contentForCopy,
      "Info provided by EnrollPilot",
    ];
    const plainText = textParts.filter(Boolean).join("\n\n");

    try {
      /* WHY CLIPBOARD WRITE: ClipboardItem with text/html preserves formatting
       * in rich-text paste targets (Facebook, Google Docs, etc.) */
      const htmlBlob = new Blob([fullHtml], { type: "text/html" });
      const textBlob = new Blob([plainText], { type: "text/plain" });
      await navigator.clipboard.write([
        new ClipboardItem({ "text/html": htmlBlob, "text/plain": textBlob }),
      ]);
    } catch {
      /* WHY FALLBACK CHAIN: clipboard.write() may be unavailable in iframes
       * without clipboard-write permission; plain text is better than nothing */
      try {
        await navigator.clipboard.writeText(plainText);
      } catch {
        const ta = document.createElement("textarea");
        ta.value = plainText;
        ta.style.cssText = "position:fixed;opacity:0;top:0;left:0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
    }

    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${
          isUser
            ? "bg-[#0A9B72] text-white"
            : "bg-gray-100 text-gray-900"
        }`}
      >
        {isUser ? (
          <p>{message.content}</p>
        ) : message.content ? (
          <>
            <div
              ref={contentRef}
              className="prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-headings:my-2 prose-hr:my-2 prose-a:text-[#0A9B72] prose-a:no-underline hover:prose-a:underline"
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  /* WHY target=_blank: widget runs inside an iframe on the marketing
                     site; without this, links navigate inside the tiny iframe instead
                     of opening in the user's browser */
                  a: ({ href, children, ...props }) => (
                    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                      {children}
                    </a>
                  ),
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={handleCopy}
                className="flex items-center gap-1 rounded-md px-2 py-0.5 text-xs text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600"
              >
                {copied ? (
                  <>
                    <svg className="h-3.5 w-3.5 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    <span className="text-green-600">Copied!</span>
                  </>
                ) : (
                  <>
                    <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                    Copy
                  </>
                )}
              </button>
            </div>
            {message.chips?.length && onChipClick ? (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {message.chips.map((chip) => (
                  <button
                    key={chip.value}
                    onClick={() => onChipClick(chip.value)}
                    disabled={chipsDisabled}
                    className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-700 hover:border-[#0A9B72] hover:text-[#0A9B72] transition-colors disabled:opacity-50"
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            ) : null}
          </>
        ) : isStreaming ? (
          <div className="flex items-center gap-1 py-1">
            <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.3s]" />
            <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.15s]" />
            <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400" />
          </div>
        ) : null}
      </div>
    </div>
  );
}
